/**
 * Full-screen dialog showing SIM metadata, tutorial video, and level navigation.
 *
 * @module components/sim/DetailsScreen@author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import CloseIcon from "@material-ui/icons/Close";
import {
    Divider,
    Button,
    Typography,
    Box,
    Tooltip,
    CircularProgress,
    DialogContent,
    DialogActions,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { connect } from "react-redux";
import Levels from "./Levels.jsx";
import {
    fetchStats,
    getAssignmentStatus,
    saveSimTitle,
    resetStats,
    resetVideoStats,
    saveCurrentSimProductType,
} from "../../redux/sims/actions";
import { useStyles } from "./DetailsScreen.js";
import { Redirect } from 'react-router-dom';
import testSpeakerAudio from '../../assets/audio/daffy.mp3';

/**
 * Represents a detailed screen component responsible for displaying information
 * regarding a specific simulation. It provides functionalities such as showing
 * tutorial videos, handling audio playback to test speakers, and rendering levels
 * or statistics associated with the simulation.
 *
 * Features:
 * - Displays detailed information about the simulation, including titles, descriptions, and brief descriptions.
 * - Offers playback of a tutorial video if available.
 * - Provides an option to test speakers with a predefined audio clip.
 * - Manages redirection behavior based on user roles and assignment data.
 * - Renders hierarchical levels fetched from simulation statistics.
 * - Displays alerts or loading indicators based on the status of level data retrieval.
 *
 * State Variables:
 * - `enableRedirect`: A boolean state that determines if a user should be redirected to the dashboard.
 * - `playTutorial`: A boolean state that toggles the visibility of the tutorial video dialog.
 *
 * Props:
 * - `sim`: Simulation object containing metadata (title, description, video URLs, documents, etc.).
 * - `open`: Boolean value to control the visibility of the details screen dialog.
 * - `handleClose`: Function to handle the dialog's closing behavior.
 * - `stats`: Object containing statistical information and levels related to the simulation.
 * - `fetchStats`: Function to fetch simulation statistics based on simulation ID and user assignment data.
 * - `getAssignmentStatus`: Function to fetch the current assignment status.
 * - `saveSimTitle`: Function to save the simulation title.
 * - `assignmentData`: Object containing metadata about the current assignment.
 * - `currentAssignmentStatus`: Object representing the current assignment's status.
 * - `resetStats`: Function to reset the simulation statistics.
 * - `status`: String representing the HTTP status of a request (e.g., "404", "500").
 * - `userDetails`: Object containing metadata about the logged-in user (e.g., role, ID).
 * - `resetVideoStats`: Function to reset video-related statistics.
 * - `saveCurrentSimProductType`: Function to save the product type of the current simulation.
 *
 * Use Cases:
 * - Viewing detailed information about a specific simulation and its associated levels.
 * - Allowing agents or reviewers to interact with simulation data and perform assigned tasks.
 * - Testing audio playback capability before participating in simulations.
 *
 * Restrictions:
 * - The `sim` prop and its nested object properties must be validated for required fields (e.g., _id, title, etc.).
 * - Assignment data and user details must be properly initialized before interacting with the component to avoid runtime errors.
 */
const DetailsScreen = ({
    sim,
    open,
    handleClose,
    stats,
    fetchStats,
    getAssignmentStatus,
    saveSimTitle,
    assignmentData,
    currentAssignmentStatus,
    resetStats,
    status,
    userDetails,
    resetVideoStats,
    saveCurrentSimProductType,
}) => {
    const classes = useStyles();
    const [enableRedirect, setEnableRedirect] = useState(false);
    const [playTutorial, setPlayTutorial] = useState(false);

    useEffect(() => {
        const userId = assignmentData?.assigned_to_id || assignmentData?.agent_user_id;
        fetchStats(sim._id, userId);
        if (sim.product_id?.name) {
            saveCurrentSimProductType(sim.product_id.name);
        }
    }, [sim._id, fetchStats, assignmentData, sim.product_id,saveCurrentSimProductType]);

    useEffect(() => {
        if (assignmentData && Object.keys(assignmentData).length > 0) {
            //const payload = assignmentData?.reviewer ?? false;
            if (assignmentData.assignment_id) {
                getAssignmentStatus(assignmentData.assignment_id);
            }
        }
        saveSimTitle(sim.title);
    }, [assignmentData, getAssignmentStatus, sim.title, saveSimTitle]);

    useEffect(() => {
        if (userDetails.role.toLowerCase() === 'agent') {
            resetVideoStats();
        }
    });

    /**
     * Plays an audio element on the webpage.
     * This function selects the first DOM element with the class name "audio-element"
     * and triggers its playback using the HTMLMediaElement `play()` method.
     *
     * Note: Ensure that an element with the class name "audio-element" exists in the DOM
     * and it is properly initialized with a valid audio source before calling this function.
     *
     * Common use cases:
     * - Used to trigger audio playback in response to a user action (e.g., button click).
     * - Useful for implementing custom audio controls within web applications.
     *
     * Restrictions:
     * - Browsers typically require a user-initiated action (e.g., click or touch) before
     *   allowing audio to play to comply with autoplay policies.
     */
    const playAudio = () => {
        const audioEl = document.getElementsByClassName("audio-element")[0];
        audioEl.play();
    };

    /**
     * Handles the closure of the detail screen with specific behaviors based on user role and assignment data.
     *
     * Resets relevant statistics and performs conditional checks:
     * - If `assignmentData` is undefined or the user's role is not 'agent', it triggers the `handleClose` function to set the closure state to false.
     * - Otherwise, enables redirection by setting the `setEnableRedirect` state to true.
     */
    const closeDetailScreen = () => {
        resetStats();
        if (!assignmentData || userDetails.role.toLowerCase() !== 'agent') {
            handleClose(false);
        } else {
            setEnableRedirect(true);
        }
    };

    if (enableRedirect && userDetails.role.toLowerCase() === 'agent') {
        return (
            <Redirect to={'/dashboard'} />
        );
    }

    if (localStorage.getItem('state')) {
        localStorage.removeItem('state');
    }

    return (
        <div>
            <Dialog
                onClose={closeDetailScreen}
                aria-labelledby="details-screen"
                open={open}
                fullWidth
                className={classes.root}
            >
                <div className={classes.modalBody}>
                    <DialogTitle id="details-screen" className={classes.title}>
                        <br />
                        {sim.title}
                    </DialogTitle>
                    <Divider className={classes.divider} />
                    <Box display="flex" justifyContent="space-between" mb={3}>
                        <div>
                            <Typography variant="body1" className={classes.description}>
                                {sim.description}
                            </Typography>
                            <Typography variant="body1" className={classes.description}>
                                {sim.brief_description}
                            </Typography>
                        </div>

                        <div>
                            {sim && sim.tutorial_video_url &&
                                <Button
                                    variant="contained"
                                    className={classes.tutorialBtn}
                                    size="small"
                                    disableElevation
                                    onClick={() => setPlayTutorial(true)}
                                >
                                    TUTORIAL VIDEO
                                </Button>
                            }

                            <Tooltip
                                title="If you hear the Daffy Duck, your speakers are good to go!"
                                placement="bottom"
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        className={classes.testSpeakerBtn}
                                        size="small"
                                        disableElevation
                                        onClick={playAudio}
                                    >
                                        TEST SPEAKERS
                                    </Button>
                                </span>
                            </Tooltip>
                        </div>
                    </Box>
                    <audio className="audio-element">
                        <source src={testSpeakerAudio} />
                    </audio>
                    {stats?.result?.levels?.length ? (
                        stats.result.levels.map((level, index) => {
                            const simType = sim.type ? sim.type : sim.sim_type
                            return (
                                <Levels
                                    level={level}
                                    key={level.level_id}
                                    index={index}
                                    simId={sim._id}
                                    document={sim.documents ? sim.documents : ''}
                                    hotLinks={sim.hot_links ? sim.hot_links : ''}
                                    captivate_learning_video_url={sim.captivate_learning_video_url ? sim.captivate_learning_video_url : ''}
                                    captivate_learning_url={sim.captivate_learning_url ?  sim.captivate_learning_url : ''}
                                    assignmentData={assignmentData}
                                    type={assignmentData?.type ? assignmentData.type : simType}
                                    currentAssignmentStatus={currentAssignmentStatus}
                                />
                            )
                        })
                    ) : Object.keys(stats).length === 0 && (status === "404" || status === "500") ? (
                        <Alert severity="warning">
                            {" "}
                            under progress! Level not yet drafted completely
                        </Alert>
                    ) : (
                        <div className={classes.loader}>
                            <CircularProgress />
                        </div>
                    )}
                </div>
                <Button
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={closeDetailScreen}
                >
                    <CloseIcon fontSize="small" />
                </Button>
            </Dialog>
            <Dialog
                open={playTutorial}
                onClose={() => { setPlayTutorial(false); }}
                maxWidth="lg"
            >
                <DialogTitle id="alert-dialog-title">Video Tutorial</DialogTitle>
                <DialogContent>
                    <video
                        width="1000"
                        height="700"
                        controls
                        autoPlay
                    >
                        <source src={sim.tutorial_video_url} type="video/mp4" />
                    </video>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setPlayTutorial(false)}
                        className={classes.cancelButton}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

/**
 * PropTypes definition for the DetailsScreen component.
 * This defines the expected props the component should receive
 * and their respective types.
 */
DetailsScreen.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    sim: PropTypes.shape({
        _id: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        brief_description: PropTypes.string,
        sim_type: PropTypes.string,
        product_id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        type: PropTypes.string,
        tutorial_video_url: PropTypes.string,
        documents: PropTypes.array,
        hot_links: PropTypes.array,
        captivate_learning_video_url: PropTypes.string,
        captivate_learning_url: PropTypes.string,
    }),
    stats: PropTypes.object,
    fetchStats: PropTypes.func,
    getAssignmentStatus: PropTypes.func,
    saveSimTitle: PropTypes.func,
    assignmentData: PropTypes.object,
    currentAssignmentStatus: PropTypes.object,
    resetStats: PropTypes.func,
    status: PropTypes.any,
    userDetails: PropTypes.object,
    resetVideoStats: PropTypes.func,
    saveCurrentSimProductType: PropTypes.func,
};

/**
 * Maps the Redux state to the props of a component.
 * Provides specific slices of the state as properties to be used within the component.
 *
 * @param {Object} state - The entire Redux state object.
 * @returns {Object} An object containing properties extracted from the Redux state:
 * - `status`: Represents the current status of the simulation.
 * - `stats`: Contains statistical data related to the simulation.
 * - `currentAssignmentStatus`: Indicates the status of the current assignment in the simulation.
 * - `userDetails`: Provides details about the logged-in user.
 */
const mapStateToProps = (state) => ({
    status: state.sim.status,
    stats: state.sim.stats,
    currentAssignmentStatus: state.sim.currentAssignmentStatus,
    userDetails: state.login.user,
});

/**
 * An object mapping dispatchable action creators to props for use in a React-Redux container component.
 * This mapping allows the component to invoke Redux actions as needed.
 *
 * Properties:
 * - fetchStats: Dispatches an action to fetch statistics data.
 * - getAssignmentStatus: Dispatches an action to retrieve the assignment status.
 * - resetStats: Dispatches an action to reset all statistical data.
 * - saveSimTitle: Dispatches an action to save the simulation title.
 * - resetVideoStats: Dispatches an action to reset video-related statistics.
 * - saveCurrentSimProductType: Dispatches an action to save the current simulation product type.
 */
const mapDispatchToProps = {
    fetchStats,
    getAssignmentStatus,
    resetStats,
    saveSimTitle: saveSimTitle,
    resetVideoStats,
    saveCurrentSimProductType,
};

export default connect(mapStateToProps, mapDispatchToProps)(DetailsScreen);
